import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode
} from "react";

type AsyncButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> & {
  /**
   * Click handler. If it returns a promise, the button shows a spinner and
   * disables itself until that promise settles — so a slow action never reads
   * as an unresponsive click.
   */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
  /**
   * Externally-controlled in-flight flag, merged with the internal one. Use it
   * for buttons whose loading lives in the parent (a form `onSubmit`, a shared
   * `publishing` state) so they still get the same spinner treatment.
   */
  busy?: boolean;
  /** Optional label swapped in while loading (defaults to `children`). */
  loadingLabel?: ReactNode;
  children?: ReactNode;
};

/**
 * A button that surfaces its own loading state. While an async `onClick` (or an
 * external `busy`) is pending it disables itself, sets `aria-busy`, and renders
 * an inline spinner ahead of the label. Consistent, click-safe feedback for
 * every action button that kicks off work.
 */
export function AsyncButton({
  onClick,
  busy = false,
  loadingLabel,
  children,
  className,
  disabled,
  type = "button",
  ...rest
}: AsyncButtonProps) {
  const [pending, setPending] = useState(false);
  // Guard against setting state after an async action navigates the button away.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loading = pending || busy;

  async function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading || disabled) return;
    const result = onClick?.(event);
    if (result && typeof (result as Promise<unknown>).then === "function") {
      setPending(true);
      try {
        await result;
      } catch {
        // Handlers own their own error UI (they set an error state); swallow here
        // so a rejected action never becomes an unhandled promise rejection.
      } finally {
        if (mounted.current) setPending(false);
      }
    }
  }

  const mergedClassName = `${className ?? ""}${loading ? " is-loading" : ""}`.trim();

  return (
    <button
      {...rest}
      type={type}
      className={mergedClassName || undefined}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={handleClick}
    >
      {loading && <span className="btn-spinner" aria-hidden="true" />}
      {loading ? loadingLabel ?? children : children}
    </button>
  );
}
